import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Eye, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
    pending: { label: 'Изчакваща', variant: 'secondary' },
    awaiting_prepayment: { label: 'Очаква предварително плащане', variant: 'secondary' },
    confirmed: { label: 'Потвърдена', variant: 'default' },
    processing: { label: 'Обработва се', variant: 'outline' },
    shipped: { label: 'Изпратена', variant: 'default' },
    delivered: { label: 'Доставена', variant: 'success' },
    cancelled: { label: 'Отказана', variant: 'destructive' }
};

const OrdersPage = () => {
    const { token } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const response = await axios.get(`${API}/orders`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOrders(response.data);
            } catch (error) {
                console.error('Error fetching orders:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="container-main py-8">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 w-48 bg-muted rounded" />
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-32 bg-muted rounded" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="container-main py-8">
                <h1 className="font-serif text-3xl md:text-4xl font-bold mb-8">Моите поръчки</h1>

                {orders.length === 0 ? (
                    <div className="text-center py-16">
                        <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <h2 className="font-serif text-xl font-bold mb-2">Нямате поръчки</h2>
                        <p className="text-muted-foreground mb-6">Все още не сте направили поръчка</p>
                        <Link to="/products">
                            <Button className="btn-primary">
                                Разгледайте продуктите
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {orders.map((order) => {
                            const status = statusLabels[order.status] || statusLabels.pending;
                            return (
                                <div
                                    key={order.order_id}
                                    className="bg-white rounded-lg border border-border p-6"
                                    data-testid={`order-${order.order_id}`}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                        <div>
                                            <p className="text-sm text-muted-foreground">
                                                Поръчка #{order.order_id}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {new Date(order.created_at).toLocaleDateString('bg-BG', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Badge variant={status.variant}>{status.label}</Badge>
                                            <div className="text-right">
                                                <span className="font-bold text-primary">{getPriceData(order.total).eurFormatted} €</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Items Preview */}
                                    <div className="flex items-center gap-3 mb-4 overflow-x-auto pb-2">
                                        {order.items.slice(0, 4).map((item) => (
                                            <img
                                                key={item.product_id}
                                                src={item.image_url}
                                                alt={item.name}
                                                className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                                            />
                                        ))}
                                        {order.items.length > 4 && (
                                            <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                                                <span className="text-sm text-muted-foreground">
                                                    +{order.items.length - 4}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-border">
                                        <p className="text-sm text-muted-foreground">
                                            {order.items.length} продукт{order.items.length !== 1 ? 'а' : ''}
                                        </p>
                                        <Link to={`/orders/${order.order_id}`}>
                                            <Button variant="ghost" className="text-secondary">
                                                Преглед
                                                <ChevronRight className="h-4 w-4 ml-1" />
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrdersPage;
